import { createApi } from "@reduxjs/toolkit/query/react";

import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type {
  IAttendanceRule,
  ICheckInContext,
  ICreateRulePayload,
  IListRulesParams,
  IListRulesResponse,
  ILookupItem,
} from "@/types/(company)/attendanceRulesV2.interface";

// BE wraps every response: { success, statusCode, message, data, meta?, timestamp }.
// Helpers below unwrap that envelope so consumers get the payload directly.
const unwrapData = <T,>(raw: any): T => raw?.data ?? raw;
const unwrapList = <T,>(raw: any): { data: T[]; meta: any } => ({
  data: raw?.data ?? [],
  meta: raw?.meta ?? raw?.data?.meta ?? {},
});

export const attendanceRulesV2Api = createApi({
  reducerPath: "attendanceRulesV2Api",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["AttendanceRules", "AttendanceRuleDefault"],
  endpoints: (builder) => ({
    listRules: builder.query<IListRulesResponse, IListRulesParams | void>({
      query: (params) => ({
        url: "/company/attendance-rules",
        method: "GET",
        params: params || undefined,
      }),
      transformResponse: (raw: any) => unwrapList<IAttendanceRule>(raw),
      providesTags: ["AttendanceRules"],
    }),

    getRuleById: builder.query<IAttendanceRule, number>({
      query: (id) => ({
        url: `/company/attendance-rules/${id}`,
        method: "GET",
      }),
      transformResponse: (raw: any) => unwrapData<IAttendanceRule>(raw),
      providesTags: (_r, _e, id) => [{ type: "AttendanceRules", id }],
    }),

    getDefaultRule: builder.query<IAttendanceRule, void>({
      query: () => ({
        url: "/company/attendance-rules/default",
        method: "GET",
      }),
      transformResponse: (raw: any) => unwrapData<IAttendanceRule>(raw),
      providesTags: ["AttendanceRuleDefault"],
    }),

    createRule: builder.mutation<IAttendanceRule, ICreateRulePayload>({
      query: (body) => ({
        url: "/company/attendance-rules",
        method: "POST",
        body,
      }),
      transformResponse: (raw: any) => unwrapData<IAttendanceRule>(raw),
      invalidatesTags: ["AttendanceRules", "AttendanceRuleDefault"],
    }),

    updateRule: builder.mutation<
      IAttendanceRule,
      { id: number; body: Partial<ICreateRulePayload> }
    >({
      query: ({ id, body }) => ({
        url: `/company/attendance-rules/${id}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (raw: any) => unwrapData<IAttendanceRule>(raw),
      invalidatesTags: ["AttendanceRules", "AttendanceRuleDefault"],
    }),

    deleteRule: builder.mutation<{ deleted: boolean }, number>({
      query: (id) => ({
        url: `/company/attendance-rules/${id}`,
        method: "DELETE",
      }),
      transformResponse: (raw: any) =>
        unwrapData<{ deleted: boolean }>(raw) ?? { deleted: true },
      invalidatesTags: ["AttendanceRules"],
    }),

    lookupUsers: builder.query<ILookupItem[], { branchId?: number } | void>({
      query: (params) => ({
        url: "/company/attendance-rules/lookup/users",
        method: "GET",
        params: params || undefined,
      }),
      transformResponse: (raw: any) => unwrapData<ILookupItem[]>(raw) ?? [],
    }),

    lookupTeams: builder.query<
      { id: number; name: string }[],
      { branchId?: number } | void
    >({
      query: (params) => ({
        url: "/company/attendance-rules/lookup/teams",
        method: "GET",
        params: params || undefined,
      }),
      transformResponse: (raw: any) =>
        unwrapData<{ id: number; name: string }[]>(raw) ?? [],
    }),

    lookupProjects: builder.query<
      { id: number; name: string }[],
      { branchId?: number } | void
    >({
      query: (params) => ({
        url: "/company/attendance-rules/lookup/projects",
        method: "GET",
        params: params || undefined,
      }),
      transformResponse: (raw: any) =>
        unwrapData<{ id: number; name: string }[]>(raw) ?? [],
    }),

    getCheckInContext: builder.query<
      ICheckInContext,
      { teamId?: number; projectId?: number } | void
    >({
      query: (params) => ({
        url: "/time-attendance/check-in-context",
        method: "GET",
        params: params || undefined,
      }),
      transformResponse: (raw: any) => unwrapData<ICheckInContext>(raw),
    }),
  }),
});

export const {
  useListRulesQuery,
  useGetRuleByIdQuery,
  useGetDefaultRuleQuery,
  useCreateRuleMutation,
  useUpdateRuleMutation,
  useDeleteRuleMutation,
  useLookupUsersQuery,
  useLookupTeamsQuery,
  useLookupProjectsQuery,
  useGetCheckInContextQuery,
  useLazyGetCheckInContextQuery,
} = attendanceRulesV2Api;
